import { useState } from 'react';
import MainLayout from '../../../components/layout/MainLayout';

interface RechargeLevel {
  id: number;
  amount: number;
  bonus: number;
}

interface RechargeConfig {
  levels: RechargeLevel[];
  allowCustomAmount: boolean;
  minAmount: number;
  maxAmount: number;
  refundRules: string;
}

export default function ProductRechargePage() {
  const [config, setConfig] = useState<RechargeConfig>({
    levels: [
      { id: 1, amount: 10, bonus: 0 },
      { id: 2, amount: 50, bonus: 0 },
      { id: 3, amount: 100, bonus: 0 },
      { id: 4, amount: 500, bonus: 0 },
      { id: 5, amount: 1000, bonus: 0 },
    ],
    allowCustomAmount: true,
    minAmount: 0,
    maxAmount: 0,
    refundRules: '',
  });

  const [newAmount, setNewAmount] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleAddLevel = () => {
    const amount = Number(newAmount);
    if (!amount || amount <= 0) {
      setErrors({ ...errors, newAmount: '请输入有效的档位金额' });
      return;
    }

    if (config.levels.some((l) => l.amount === amount)) {
      setErrors({ ...errors, newAmount: '该档位金额已存在' });
      return;
    }

    const newLevel: RechargeLevel = {
      id: Math.max(0, ...config.levels.map((l) => l.id)) + 1,
      amount,
      bonus: 0,
    };

    setConfig({
      ...config,
      levels: [...config.levels, newLevel].sort((a, b) => a.amount - b.amount),
    });
    setNewAmount('');
    setErrors({ ...errors, newAmount: '' });
  };

  const handleDeleteLevel = (id: number) => {
    setConfig({
      ...config,
      levels: config.levels.filter((l) => l.id !== id),
    });
  };

  const handleBonusChange = (id: number, bonus: number) => {
    setConfig({
      ...config,
      levels: config.levels.map((l) => (l.id === id ? { ...l, bonus } : l)),
    });
  };

  const handleSave = () => {
    const newErrors: Record<string, string> = {};

    if (config.levels.length === 0) {
      newErrors.levels = '请至少添加一个充值档位';
    }

    if (config.allowCustomAmount) {
      if (config.minAmount < 0 || config.minAmount > 1000) {
        newErrors.minAmount = '最小充值金额范围为0~1000';
      }

      if (config.maxAmount < 0) {
        newErrors.maxAmount = '最大充值金额不能为负数';
      }

      if (config.maxAmount > 0 && config.maxAmount < config.minAmount) {
        newErrors.maxAmount = '最大充值金额不能小于最小充值金额';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    // 模拟保存
    const toast = document.createElement('div');
    toast.className = 'fixed top-4 right-4 bg-teal-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
    toast.textContent = '保存成功';
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 2000);
  };

  return (
    <MainLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">充值管理</h1>
          <p className="text-sm text-gray-500 mt-1">配置充值档位和充值规则</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 mb-4">档位预设</h2>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                新增档位
              </label>
              <div className="flex gap-3">
                <div className="flex-1">
                  <div className="relative">
                    <input
                      type="number"
                      value={newAmount}
                      onChange={(e) => {
                        setNewAmount(e.target.value);
                        setErrors({ ...errors, newAmount: '' });
                      }}
                      placeholder="请输入档位金额"
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                        errors.newAmount ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
                      元
                    </span>
                  </div>
                  {errors.newAmount && (
                    <p className="mt-1 text-sm text-red-500">{errors.newAmount}</p>
                  )}
                </div>
                <button
                  onClick={handleAddLevel}
                  className="px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap"
                >
                  添加
                </button>
              </div>
            </div>

            {errors.levels && (
              <p className="mb-4 text-sm text-red-500">{errors.levels}</p>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">
                      档位金额
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">
                      赠送金额
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">
                      实际到账
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {config.levels.map((level) => (
                    <tr key={level.id}>
                      <td className="px-4 py-3 text-gray-900">¥{level.amount}</td>
                      <td className="px-4 py-3">
                        <div className="relative w-32">
                          <input
                            type="number"
                            value={level.bonus}
                            onChange={(e) =>
                              handleBonusChange(level.id, Number(e.target.value))
                            }
                            placeholder="0"
                            className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs">
                            元
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-teal-600 font-medium">
                        ¥{level.amount + level.bonus}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleDeleteLevel(level.id)}
                          className="text-red-600 hover:text-red-700 text-sm whitespace-nowrap"
                        >
                          删除
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="p-6 border-b border-gray-200 space-y-6">
            <h2 className="text-lg font-medium text-gray-900">配置项</h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                是否支持自定义金额充值
              </label>
              <div className="flex gap-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    checked={config.allowCustomAmount === true}
                    onChange={() => setConfig({ ...config, allowCustomAmount: true })}
                    className="w-4 h-4 text-teal-500 border-gray-300 focus:ring-teal-500 cursor-pointer"
                  />
                  <span className="ml-2 text-sm text-gray-700">是</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    checked={config.allowCustomAmount === false}
                    onChange={() => setConfig({ ...config, allowCustomAmount: false })}
                    className="w-4 h-4 text-teal-500 border-gray-300 focus:ring-teal-500 cursor-pointer"
                  />
                  <span className="ml-2 text-sm text-gray-700">否</span>
                </label>
              </div>
            </div>

            {config.allowCustomAmount && (
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    最小充值金额/笔
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={config.minAmount}
                      onChange={(e) => {
                        setConfig({ ...config, minAmount: Number(e.target.value) });
                        setErrors({ ...errors, minAmount: '' });
                      }}
                      placeholder="0~1000，默认为0即不限制"
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                        errors.minAmount ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
                      元
                    </span>
                  </div>
                  {errors.minAmount && (
                    <p className="mt-1 text-sm text-red-500">{errors.minAmount}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    最大充值金额/笔
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={config.maxAmount}
                      onChange={(e) => {
                        setConfig({ ...config, maxAmount: Number(e.target.value) });
                        setErrors({ ...errors, maxAmount: '' });
                      }}
                      placeholder="默认为0即不限制"
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                        errors.maxAmount ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
                      元
                    </span>
                  </div>
                  {errors.maxAmount && (
                    <p className="mt-1 text-sm text-red-500">{errors.maxAmount}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 mb-4">退款规则</h2>
            <textarea
              value={config.refundRules}
              onChange={(e) => setConfig({ ...config, refundRules: e.target.value })}
              placeholder="请输入退款规则说明"
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div className="flex justify-end gap-3 px-6 pb-6 pt-4">
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap"
            >
              保存
            </button>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
